import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addSubjectComment,
  createSubjectReviewQueryKey,
  fetchSubjectReview,
} from "@/api/documentReview";
import { invalidateCleDocumentQueries } from "@/features/cle-compliance/hooks/invalidateCleDocumentQueries";
import type { DocumentCommentModel } from "@/features/cle-compliance/models/documentComment";
import type {
  AddSubjectCommentRequest,
  SubjectReviewModel,
} from "@/features/cle-compliance/models/documentReview";

export {
  createSubjectReviewQueryKey,
  SUBJECT_REVIEW_QUERY_KEY,
} from "@/api/documentReview";

export type AddSubjectCommentMutationRequest = AddSubjectCommentRequest & {
  subjectId: string;
};

export const useSubjectReview = (subjectId: string) =>
  useQuery<SubjectReviewModel>({
    enabled: subjectId.length > 0,
    queryKey: createSubjectReviewQueryKey(subjectId),
    queryFn: () => fetchSubjectReview(subjectId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useAddSubjectComment = () => {
  const queryClient = useQueryClient();

  return useMutation<
    DocumentCommentModel,
    Error,
    AddSubjectCommentMutationRequest
  >({
    mutationFn: ({ subjectId, content }) =>
      addSubjectComment(subjectId, { content }),
    onSuccess: (_data, { subjectId }) => {
      void queryClient.invalidateQueries({
        queryKey: createSubjectReviewQueryKey(subjectId),
      });
      invalidateCleDocumentQueries(queryClient);
    },
  });
};
